import { NgClass, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationService } from '@sc/data-access-configuration';
import { InsightService } from '@sc/data-access-http-services';
import {
  canRepairImpactsType,
  Claim,
  Document,
  Dossier,
  getGlassNameGender,
  getOperationType,
  Glass,
  GlassExtended,
  GlassUsageConfiguration,
  isOperationTypeChanged,
  SinisterGlass,
  PicturesViewerData,
  getLatestDossierCommentTextByType,
  DossierCommentsEnum,
  DossierComments,
} from '@sc/data-access-models';
import {
  AppointmentsSandboxService,
  DossierSandboxService,
  SandboxService,
} from '@sc/data-access-state-management';
import { TranslatorService } from '@sc/data-access-translation';
import {
  FeatureFlippingManagerService,
  FeatureToggleDirective,
} from '@sc/feature-flipping';
import { LoadingService } from '@sc/feature-loader';
import { PicturesViewerService } from '@sc/feature-pictures-viewer';
import { SnackbarService } from '@sc/feature-snackbar';
import {
  ButtonComponent,
  CarImgSummaryComponent,
  ClaimPicturesComponent,
  IconComponent,
} from '@sc/ui-components';
import { InsurerDeclarationSteps } from '../../../../models';
import { SideSummaryComponent } from '../side-summary/side-summary.component';
import { SinisterDeclarationAccordionPanelComponent } from './components/sinister-declaration-accordion-panel/sinister-declaration-accordion-panel.component';
import { AuthService } from '@sc/feature-auth-okta';

@Component({
  selector: 'sc-sinister-data-form',
  styleUrls: ['./sinister-data-form.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SideSummaryComponent,
    NgIf,
    IconComponent,
    ReactiveFormsModule,
    FeatureToggleDirective,
    SinisterDeclarationAccordionPanelComponent,
    NgClass,
    ButtonComponent,
    ClaimPicturesComponent,
    TranslateModule,
    CarImgSummaryComponent,
  ],
  template: `
    <h1 class="sc-design-title-h1">
      {{ 'SinisterDataForm.fillInfosTitle' | translate }}
    </h1>

    <div class="sides">
      <div class="left">
        <sc-side-summary
          [isValidCustomerData]="true"
          [dossier]="currentDossier"
        ></sc-side-summary>
        <div
          *ngIf="
            featureFlippingService.isFeatureActivated(
              'b2b-allow-document-management-insurer'
            )
          "
        >
          <span
            id="addPhotoButton"
            class="cta-btn"
            *ngIf="
              form.valid &&
              !displayPictureUploadForm &&
              featureFlippingService.isFeatureActivated(
                'b2b-display-add-photo-button'
              )
            "
            tabindex="0"
            role="button"
            (click)="displayPictureUploadForm = true"
            (keydown.enter)="displayPictureUploadForm = true"
            (keydown.space)="displayPictureUploadForm = true"
          >
            <sc-icon name="picture_preview"></sc-icon>
            <span>{{ 'SinisterDataForm.addPhotoBtn' | translate }}</span>
          </span>
          <span
            id="returnSinisterFormButton"
            class="cta-btn"
            *ngIf="displayPictureUploadForm"
            tabindex="0"
            role="button"
            (click)="displayPictureUploadForm = false"
            (keydown.enter)="displayPictureUploadForm = false"
            (keydown.space)="displayPictureUploadForm = false"
          >
            <sc-icon name="folder"></sc-icon>
            <span>{{ 'SinisterDataForm.goBackToDossierBtn' | translate }}</span>
          </span>
        </div>
      </div>

      <form class="right" [formGroup]="form">
        <ng-container *ngIf="!displayPictureUploadForm">
          <div class="sc-form-title-desktop sc-design-title-h2">
            {{ 'SinisterDataForm.sinisterTypeTitle' | translate }}
          </div>

          <div style="display:flex; flex-direction:row;">
            <sc-car-img-summary
              [glass]="currentDossier?.claim?.glass"
              (glassChangeEvent)="onGlassChange($event)"
              [editMode]="true"
            ></sc-car-img-summary>

            <div class="sinister-declaration-qualification">
              <div
                class="multi-damage-message-panel"
                *scFeatureToggle="['b2b-display-sinister-multi-damage-info']"
              >
                <p class="multi-damage-title">
                  {{ 'SinisterDataForm.multiDamageTitle' | translate }}
                </p>
                <p class="multi-damage-description">
                  {{ 'SinisterDataForm.multiDamageDescription' | translate }}
                </p>
              </div>
              <sc-sinister-declaration-accordion-panel
                [parentForm]="form"
                [updateDisplay]="updateDisplay"
                [isFormError]="isFormError"
                [isReparation]="isReparation"
                (glassChangeEvent)="onGlassChange($event)"
                (excessChangeEvent)="excessChange()"
              >
              </sc-sinister-declaration-accordion-panel>

              <ng-container *ngIf="form.valid">
                <div
                  class="operation-type-container"
                  [ngClass]="{
                    'operation-replacement': !isReparation,
                    'operation-repair': isReparation
                  }"
                  *scFeatureToggle="[
                    'b2b-display-sinister-operation-type-info'
                  ]"
                >
                  <div class="operation-type-icon-container">
                    <sc-icon name="info"></sc-icon>
                  </div>
                  <div class="operation-type-text-container">
                    <h3 class="sc-design-text-1">Information</h3>
                    <p class="sc-design-text-3" *ngIf="!isReparation">
                      {{
                        'SinisterDataForm.operationType.replacement'
                          | translate
                            : {
                                glassName: operationTypeGlassName,
                                gender: glassNameGender
                              }
                      }}
                    </p>
                    <p class="sc-design-text-3" *ngIf="isReparation">
                      {{
                        'SinisterDataForm.operationType.repair'
                          | translate
                            : {
                                glassName: operationTypeGlassName,
                                gender: glassNameGender
                              }
                      }}
                    </p>
                  </div>
                </div>
              </ng-container>

              <div class="button">
                <sc-button
                  type="button"
                  id="nextButton"
                  [buttonType]="form.valid ? 'primary' : 'grey'"
                  (click)="onValidate()"
                  >{{ 'SinisterDataForm.chooseAppointment' | translate }}
                </sc-button>
              </div>
            </div>
          </div>
        </ng-container>

        <ng-container
          *ngIf="
            displayPictureUploadForm &&
            featureFlippingService.isFeatureActivated(
              'b2b-allow-document-management-insurer'
            )
          "
        >
          <div class="sc-form-title-desktop">
            {{ 'SinisterDataForm.addPhotoTitle' | translate }}
          </div>

          <sc-claim-pictures
            [dossier]="currentDossier"
            (updatePictures)="onUpdatePictures($event)"
            (showPictures)="onShowPictures($event)"
          ></sc-claim-pictures>
        </ng-container>
      </form>
    </div>
  `,
})
export class SinisterDataFormComponent implements OnInit, OnDestroy {
  form: FormGroup = this.formBuilder.group({
    glass: [null, Validators.required],
    glassExtended: [null],
    impacts: this.formBuilder.group({
      impactsType: [null],
      size: [null],
      inVisionField: [null],
      atOuterEdge: [null],
    }),
    lateralPrecisions: this.formBuilder.group({
      frontConductor: [null],
      precision: [null],
    }),
    mirror: [null],
    light: this.formBuilder.group({
      lateral: [null],
      lightsEmplacement: [null],
    }),
    sinisterComment: [null],
    sinisterExcess: [null],
  });

  glassUsage: GlassUsageConfiguration;

  currentDossier: Dossier = null;
  pictures: Document[] = [];

  updateDisplay: boolean;
  isReparation = false;
  operationTypeGlassName: string;
  glassNameGender: string;
  displayPictureUploadForm = false;
  isFormError = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly dossierSB: DossierSandboxService,
    private readonly sandbox: SandboxService,
    private readonly loadingService: LoadingService,
    private readonly router: Router,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly picturesViewerService: PicturesViewerService,
    private readonly snackbarService: SnackbarService,
    private readonly translator: TranslatorService,
    private readonly insightService: InsightService,
    private readonly authService: AuthService,
    public featureFlippingService: FeatureFlippingManagerService,
    private readonly appointmentsSB: AppointmentsSandboxService,
    private readonly configuration: ConfigurationService
  ) {
    this.insightService.trackPageView({
      name: 'Sinister data form',
    });

    this.initNavigation();
  }

  initNavigation() {
    this.sandbox.setCurrentStep(InsurerDeclarationSteps.claimFolderStep);
  }

  ngOnInit(): void {
    this.snackbarService.setFocusManagement(true, {
      form: this.form,
      documentQuery: 'sc-sinister-data-form form',
    });
    window.scrollTo(0, 0);

    this.glassUsage = this.configuration.getGlassUsage();

    this.dossierSB.dossier$.subscribe((dossier: Dossier) => {
      this.currentDossier = dossier;

      if (this.currentDossier?.claim?.glass) {
        const {
          glass,
          glassExtended,
          impacts,
          lateralPrecisions,
          light,
          mirror,
        } = this.currentDossier.claim.glass;
        this.currentDossier = {
          ...this.currentDossier,
          claim: {
            ...this.currentDossier.claim,
            operationType: getOperationType(dossier?.claim?.glass),
          },
        };

        this.isReparation =
          this.currentDossier.claim.operationType === 'Repair';
        this.operationTypeGlassName = this.getOperationTypeContainerGlassName(
          dossier?.claim?.glass
        );
        this.glassNameGender = getGlassNameGender(dossier?.claim?.glass);

        this.form.setValue({
          glass: glass,
          glassExtended: glassExtended || null,
          impacts: {
            impactsType: null,
            size: null,
            inVisionField: null,
            atOuterEdge: null,
            ...impacts,
          },
          lateralPrecisions: {
            precision: null,
            frontConductor: null,
            ...lateralPrecisions,
          },
          light: { lightsEmplacement: null, lateral: null, ...light },
          mirror: mirror ?? null,
          sinisterComment: getLatestDossierCommentTextByType(
            this.currentDossier,
            [DossierCommentsEnum.sinister]
          ),
          sinisterExcess:
            (this.isReparation
              ? this.currentDossier.policyInformation?.repairDeductible
              : this.currentDossier.policyInformation?.replacementDeductible) ||
            null,
        });
        this.formValidationDependingOfGlassChoice(glass, glassExtended);
      }
      if (!this.pictures?.length) {
        const claimPictures =
          this.dossierSB.getClaimPicturesFromDossierDocuments(
            this.currentDossier?.claim?.documents
          );
        this.pictures = [...claimPictures];
      }

      this.changeDetectorRef.markForCheck();
    });
  }

  ngOnDestroy(): void {
    this.snackbarService.setFocusManagement(false);
  }

  setActiveControlValidation(control: AbstractControl, active: boolean): void {
    control?.setValidators(active ? [Validators.required] : null);
    control?.updateValueAndValidity();
  }

  resetValidators(): void {
    for (const control in this.form.value) {
      const subForm = this.form.get(control);
      if (control !== 'glass' && control !== 'glassExtended') {
        this.setActiveControlValidation(subForm, false);

        if (control !== 'mirror') {
          for (const subControl in subForm.value) {
            this.setActiveControlValidation(subForm.get(subControl), false);
          }
        }
      }
    }
  }

  onGlassChange(answers: any) {
    const glass = answers.glass as Glass;
    const glassExtended = answers.glassExtended as GlassExtended;

    const newGlass: SinisterGlass = {
      ...this.currentDossier.claim.glass,
      glass: glass,
      glassExtended: glassExtended || null,
      impacts:
        glass === 'windshield'
          ? Object.keys(answers.sub).length > 0
            ? answers.sub
            : this.currentDossier.claim.glass?.impacts
          : null,
      lateralPrecisions: glass === 'sideWindow' ? answers.sub : null,
      light: glass === 'lights' ? answers.sub : null,
      mirror: glass === 'mirror' ? answers.sub?.mirror : null,
    };

    this.dossierSB
      .patchDossier({
        ...this.currentDossier,
        claim: {
          ...this.currentDossier.claim,
          glass: newGlass,
          operationType: getOperationType(newGlass),
        },
        dossierComments: this.getUpdatedSinisterComments(),
      })
      .subscribe(() => this.changeDetectorRef.markForCheck());
    this.autoCloseAndOpenPanels(!answers.isDisplayUpdated);
  }

  formValidationDependingOfGlassChoice(
    glassChoice: Glass,
    glassExtendedChoice: GlassExtended
  ) {
    this.resetValidators();
    switch (glassChoice) {
      case 'windshield':
        const subForm = this.form.get('impacts');
        this.setActiveControlValidation(subForm, true);
        this.setActiveControlValidation(subForm.get('impactsType'), true);
        if (canRepairImpactsType(subForm.get('impactsType')?.value)) {
          this.setActiveControlValidation(subForm.get('size'), true);
          if (subForm.get('size')?.value === 'smaller2cm') {
            this.setActiveControlValidation(subForm.get('inVisionField'), true);
            if (subForm.get('inVisionField')?.value === false) {
              this.setActiveControlValidation(subForm.get('atOuterEdge'), true);
            }
          }
        }
        break;
      case 'sideWindow':
        this.requireSubForm(
          this.form.get('lateralPrecisions'),
          'sideWindow',
          glassExtendedChoice
        );
        break;
      case 'lights':
        this.requireSubForm(
          this.form.get('light'),
          'lights',
          glassExtendedChoice
        );
        break;
      case 'mirror':
        if (
          this.glassUsage.treeDisplay.find(
            (glassDisplayElement) =>
              glassDisplayElement.glass === 'mirror' &&
              (!glassExtendedChoice ||
                glassExtendedChoice === glassDisplayElement.glassExtended) &&
              glassDisplayElement.sub?.length > 0
          )
        ) {
          this.setActiveControlValidation(this.form.get('mirror'), true);
        }
        break;
    }
  }

  requireSubForm(
    control: AbstractControl,
    glass: Glass,
    glassExtended: GlassExtended
  ) {
    this.setActiveControlValidation(control, true);
    for (const glassDisplayElement of this.glassUsage.treeDisplay) {
      if (
        glassDisplayElement.glass === glass &&
        (!glassExtended ||
          glassExtended === glassDisplayElement.glassExtended) &&
        glassDisplayElement?.sub
      ) {
        for (const glassQuestion of glassDisplayElement?.sub) {
          this.setActiveControlValidation(
            control?.get(glassQuestion?.key),
            true
          );
        }
      }
    }
  }

  excessChange() {
    const excessChange = this.form.get('sinisterExcess').value;

    this.dossierSB
      .patchDossier({
        ...this.currentDossier,
        policyInformation: {
          ...this.currentDossier.policyInformation,
          repairDeductible:
            this.isReparation && !!excessChange
              ? excessChange
              : this.currentDossier.policyInformation.repairDeductible,
          replacementDeductible:
            !this.isReparation && !!excessChange
              ? excessChange
              : this.currentDossier.policyInformation.replacementDeductible,
          isCovered: true,
        },
      })
      .subscribe(() => this.changeDetectorRef.markForCheck());
  }

  async onValidate() {
    if (this.form.invalid) {
      this.isFormError = true;
      return;
    }

    this.loadingService.setLoading(true);

    this.sandbox.uploadDocuments(this.pictures ?? []).subscribe(
      (newPictures) => {
        const claim: Partial<Claim> = {
          glass: {
            glass: this.form.get('glass').value,
            glassExtended: this.form.get('glassExtended').value,
            impacts: this.form.get('impacts').value,
            lateralPrecisions: this.form.get('lateralPrecisions').value,
            light: this.form.get('light').value,
            mirror: this.form.get('mirror').value,
          },
        };

        if (isOperationTypeChanged(this.currentDossier.claim, claim)) {
          this.appointmentsSB.clearAppointments();
        }

        // We concat other dossier documents with claim pictures
        let documents = this.dossierSB.getNotClaimPicturesFromDossierDocuments(
          this.currentDossier.claim.documents
        );

        documents = [
          ...documents.filter(
            (doc) => !newPictures.some((newPic) => newPic.type === doc.type)
          ),
          ...newPictures,
        ];

        this.dossierSB.patchDossier({
          ...this.currentDossier,
          claim: {
            ...this.currentDossier.claim,
            ...claim,
            operationType: getOperationType(claim.glass),
            documents,
          },
          policyInformation: {
            ...this.currentDossier.policyInformation,
          },
          dossierComments: this.getUpdatedSinisterComments(),
        });

        this.dossierSB.updateDossier().subscribe(
          (dossier) => {
            this.dossierSB.setDossier({
              ...dossier,
              id: dossier?.id,
              appointment: null,
            });
            this.loadingService.setLoading(false);
            this.navigateToIntervention();
          },
          () => {
            this.snackbarService.showError(
              this.translator.getInstantTranslation('Snackbar.errorTitle'),
              this.translator.getInstantTranslation(
                'GlobalErrors.dossierNotUpdated'
              )
            );
            this.loadingService.setLoading(false);
          }
        );

        this.changeDetectorRef.markForCheck();
      },
      () => {
        this.snackbarService.showError(
          this.translator.getInstantTranslation('Snackbar.errorTitle'),
          this.translator.getInstantTranslation('UploadFile.uploadError')
        );
        this.loadingService.setLoading(false);
      }
    );
  }

  private getUpdatedSinisterComments(): DossierComments[] {
    const comments = this.currentDossier?.dossierComments || [];
    const comment = (this.form.get('sinisterComment')?.value || '').trim();
    const latestComment = getLatestDossierCommentTextByType(
      this.currentDossier,
      [DossierCommentsEnum.sinister]
    ).trim();

    if (!comment || comment === latestComment) {
      return comments;
    }

    return [
      ...comments,
      {
        userName:
          `${this.authService.getAccountLastName()} ${this.authService.getAccountFirstName()}`.trim(),
        userType: 'INSURER',
        comment,
        timestamp: new Date().toISOString(),
        commentType: DossierCommentsEnum.sinister,
        isPinned: false,
      },
    ];
  }

  onUpdatePictures(pictures: Document[]) {
    this.pictures = pictures;
    this.currentDossier = {
      ...this.currentDossier,
      claim: { ...this.currentDossier?.claim, documents: pictures },
    };
  }

  onShowPictures(data: PicturesViewerData) {
    const document = data.pictures[data.index];
    if (document.metadata.mimeType === 'application/pdf') {
      const documentUrl = window.URL.createObjectURL(
        new Blob([document.file], { type: document.metadata.mimeType })
      );

      window.open(documentUrl, '_blank');
    } else {
      this.picturesViewerService.show({
        pictures: data.pictures,
        index: data.index,
        allowUnsupported: true,
      });
    }
  }

  navigateToIntervention() {
    this.router
      .navigate(['/declaration/intervention'])
      .catch((error) => console.error(error));
  }

  autoCloseAndOpenPanels(autoCloseAndOpenPanels: boolean) {
    if (autoCloseAndOpenPanels) {
      this.updateDisplay = !this.updateDisplay;

      this.changeDetectorRef.markForCheck();
    }
  }

  private getOperationTypeContainerGlassName(glass: SinisterGlass): string {
    const glassName = this.translator
      .getInstantTranslation(
        `SinisterDeclarationAccordionPanel.glass.answers.${
          glass?.glassExtended || glass?.glass
        }`
      )
      .toLowerCase();
    const singularGlassNameKey = `SinisterDeclarationAccordionPanel.glass.singular.${glass?.glass}`;
    const singularGlassName = this.translator
      .getInstantTranslation(singularGlassNameKey)
      .toLowerCase();
    if (singularGlassName !== singularGlassNameKey.toLowerCase()) {
      return singularGlassName;
    }
    return glassName;
  }
}
